import { useEffect } from "react";
import { SITE_URL } from "@/lib/config";
import { OG_IMAGE } from "@/lib/seo";

const upsertMeta = (attr, key, content) => {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!content) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Head manager without extra dependencies: it also works with the prerender
 * pass, which snapshots the DOM after the effects have run.
 */
const Seo = ({
  title,
  description,
  path = "/",
  image = OG_IMAGE,
  type = "website",
  noindex = false,
  lang = "es",
  jsonLd = null,
}) => {
  const ldKey = JSON.stringify(jsonLd);

  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    document.title = title;
    document.documentElement.lang = lang === "en" ? "en" : "es";

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    upsertLink("canonical", url);

    upsertMeta("property", "og:site_name", "Naguarà");
    upsertMeta("property", "og:locale", lang === "en" ? "en_GB" : "es_ES");
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:image:alt", "Naguarà · café de especialidad");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    document.head
      .querySelectorAll('script[data-seo="jsonld"]')
      .forEach((n) => n.remove());
    const blocks = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);
    blocks.forEach((block) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo", "jsonld");
      s.textContent = JSON.stringify(block);
      document.head.appendChild(s);
    });
  }, [title, description, path, image, type, noindex, lang, ldKey, jsonLd]);

  return null;
};

export default Seo;
